import { DOZER_CONFIG } from '../config/dozer.config';
import { HAZARDS_CONFIG } from '../config/hazards.config';
import type { ObstacleDefinition } from './ObstacleDefinition';
import { ObstacleType } from './ObstacleType';

/** Size of the big cable drum against its model (Ø 1.6 → 1.76 m) – standing obstacle and rolling hazard alike. */
export const CABLE_REEL_SCALE = 1.1;
/** The low cone row of the scaffold passage: 0.75 → 0.66 m, the height of the scaffold tubes it replaced. */
const LOW_CONE_SCALE = 0.88;

/**
 * OBS_CONTAINER_A (Blender: OBS_Container_A, shared container kit): a 20 ft standard container
 * (ISO 668 22G1, body 2.59 m) set down on 0.10 m timber sleepers. Its roof is walkable and lies
 * within the double jump's reach (step limit 2.7 m) – user 2026-10-02: "niedriger, dass man auf
 * den Containern auch mit dem Doppelsprung draufkommt" (was a 3.40 m high cube on cribs).
 * The collider is narrower than the real 2.44 m container (forgiving).
 */
export const CONTAINER_OBSTACLE = {
  /** Roof height: sleepers 0.10 m + container body 2.59 m. */
  height: 2.69,
  /** Collider across the lane (the visual is 2.44 m wide). */
  width: 2,
  /** Collider along the track (the 20 ft container is 6.06 m long). */
  length: 6,
  /** Timber sleepers under the container ends (greybox fallback). */
  sleeperHeight: 0.1,
} as const;

/**
 * Greybox obstacle set. Heights are tuned against the player physics:
 *   jump apex 1.5 m, double jump apex ≈ 3.0 m, standing height 1.8 m, sliding 0.9 m.
 */
export const DEFAULT_OBSTACLES: readonly ObstacleDefinition[] = [
  {
    id: 'OBS_BARRIER_A',
    type: ObstacleType.Jump,
    displayName: 'Betonleitwand',
    visualAssetId: 'OBS_BARRIER_A',
    // Matches the Blender model: two 0.8 m plastic barrier elements, slightly angled.
    colliders: [{ center: { x: 0, y: 0.4, z: 0 }, size: { x: 2, y: 0.8, z: 0.6 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['barrier'],
  },
  {
    // Reference sheet SEG_01 "Leitkegel": three cones across the lane (Blender: OBS_ConeRow_A).
    id: 'OBS_CONE_ROW_A',
    type: ObstacleType.Jump,
    displayName: 'Leitkegel-Reihe',
    visualAssetId: 'OBS_CONE_ROW_A',
    colliders: [{ center: { x: 0, y: 0.375, z: 0 }, size: { x: 1.9, y: 0.75, z: 0.4 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['cone'],
  },
  {
    // The cone row at 88 %: 0.66 m high – the size of the scaffold tubes it replaces in the scaffold passage (user
    // 2026-10-04: the thin tubes are hard to see at speed and at night; orange cones with reflective bands are not).
    // Its own id: the passage's layouts are proven for exactly this height right behind the crossbars (jumpHonesty).
    id: 'OBS_CONE_ROW_LOW_A',
    type: ObstacleType.Jump,
    displayName: 'Leitkegel-Reihe (niedrig)',
    visualAssetId: 'OBS_CONE_ROW_A',
    visualScale: LOW_CONE_SCALE,
    colliders: [{ center: { x: 0, y: 0.375 * LOW_CONE_SCALE, z: 0 }, size: { x: 1.9 * LOW_CONE_SCALE, y: 0.75 * LOW_CONE_SCALE, z: 0.4 * LOW_CONE_SCALE } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 0, // placed by the scaffold passage only
    tags: ['cone'],
  },
  {
    // Reference sheet SEG_01 "Betonrohr": DN 800 pipe lying across the lane (Blender: OBS_ConcretePipe_A).
    id: 'OBS_CONCRETE_PIPE_A',
    type: ObstacleType.Jump,
    displayName: 'Betonrohr',
    visualAssetId: 'OBS_CONCRETE_PIPE_A',
    colliders: [{ center: { x: 0, y: 0.56, z: 0 }, size: { x: 2, y: 1.12, z: 1.12 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['pipe'],
  },
  {
    // Reference sheet SEG_01 "Big Bag": FIBC filled with sand, loops tied on top (Blender: OBS_BigBag_A).
    id: 'OBS_BIG_BAG_A',
    type: ObstacleType.Jump,
    displayName: 'Big Bag',
    visualAssetId: 'OBS_BIG_BAG_A',
    // Body ~1.0 m; the soft loops above are not part of the collider.
    colliders: [{ center: { x: 0, y: 0.55, z: 0 }, size: { x: 1, y: 1.1, z: 1 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['bag'],
  },
  {
    // Reference sheet SEG_06 "Gerüststange (unten)": low scaffold tube hurdle (Blender: OBS_ScaffoldPole_A).
    id: 'OBS_SCAFFOLD_POLE_A',
    type: ObstacleType.Jump,
    displayName: 'Gerüststange',
    visualAssetId: 'OBS_SCAFFOLD_POLE_A',
    colliders: [{ center: { x: 0, y: 0.33, z: 0 }, size: { x: 1.9, y: 0.66, z: 0.3 } }],
    laneSpan: 1,
    clearedOnHit: true,
    // retired: the thin tubes are hard to see at speed and at night (user 2026-10-04) – the barrier board takes its place
    weight: 0,
    tags: ['scaffold'],
  },
  {
    // Reference sheets SEG_03 ff. "Betonblock": interlocking concrete block (Blender: ENV_ConcreteBlock_A).
    id: 'OBS_CONCRETE_BLOCK_A',
    type: ObstacleType.Jump,
    displayName: 'Betonblock',
    visualAssetId: 'ENV_CONCRETE_BLOCK_A',
    colliders: [{ center: { x: 0, y: 0.44, z: 0 }, size: { x: 1.6, y: 0.88, z: 0.8 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['concrete'],
  },
  {
    // Reference sheet SEG_03 (in-game view): Absperrschranke across the lane (Blender: OBS_BarrierBoard_A).
    id: 'OBS_BARRIER_BOARD_A',
    type: ObstacleType.Jump,
    displayName: 'Absperrschranke',
    visualAssetId: 'OBS_BARRIER_BOARD_A',
    colliders: [{ center: { x: 0, y: 0.5, z: 0 }, size: { x: 2, y: 1, z: 0.3 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['barrier'],
  },
  {
    // Element library "Kabeltrommel" (6 sheets): wooden cable drum Ø 1.6 m, axis across the lane
    // (Blender: OBS_CableReel_A). Too tall for a single jump.
    id: 'OBS_CABLE_REEL_A',
    type: ObstacleType.DoubleJump,
    displayName: 'Kabeltrommel',
    visualAssetId: 'OBS_CABLE_REEL_A',
    // drum only – the loose cable end on the ground does not collide. 10 % bigger than the model (user 2026-10-03):
    // Ø 1.76 m, the collider at that size – 1.74 m high: the drum's visible top over the running line is 1.61 m
    // (jump honesty: ≤ 0.15 m above it)
    visualScale: CABLE_REEL_SCALE,
    colliders: [{ center: { x: 0, y: 0.79 * CABLE_REEL_SCALE, z: 0 }, size: { x: 1.3 * CABLE_REEL_SCALE, y: 1.58 * CABLE_REEL_SCALE, z: 1.6 * CABLE_REEL_SCALE } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['cable'],
  },
  {
    // Reference sheet SEG_01 "Palette": four stacked Euro pallets (Blender: OBS_Pallet_A).
    id: 'OBS_PALLET_A',
    type: ObstacleType.Jump,
    displayName: 'Palettenstapel (4 Europaletten)',
    visualAssetId: 'OBS_PALLET_A',
    colliders: [{ center: { x: 0, y: 0.29, z: 0 }, size: { x: 1.2, y: 0.58, z: 0.8 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['pallet'],
  },
  {
    id: 'OBS_SCAFFOLD_PASS_A',
    type: ObstacleType.Slide,
    displayName: 'Gerüstdurchgang (Querstrebe)',
    visualAssetId: 'OBS_SCAFFOLD_PASS_A',
    colliders: [
      // SEG_06: striped crossbar with 1.6 m clearance, scaffold deck + rails above (no jumping over)
      { center: { x: 0, y: 2.775, z: 0 }, size: { x: 2.2, y: 2.35, z: 0.9 } },
      // posts at the lane edges – lane changes through them cause a stumble
      { center: { x: -1.08, y: 0.8, z: 0 }, size: { x: 0.1, y: 1.6, z: 0.8 } },
      { center: { x: 1.08, y: 0.8, z: 0 }, size: { x: 0.1, y: 1.6, z: 0.8 } },
    ],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['scaffold'],
  },
  {
    id: 'OBS_PALLET_STACK_A',
    type: ObstacleType.DoubleJump,
    displayName: 'Palettenstapel',
    visualAssetId: 'OBS_PALLET_STACK_A',
    colliders: [{ center: { x: 0, y: 1.2, z: 0 }, size: { x: 1.3, y: 2.4, z: 1.1 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 0, // greybox, replaced by OBS_BRICK_STACK_A – kept for existing references
    tags: ['pallet'],
  },
  {
    // Reference sheet SEG_01 "Ziegelstapel": three pallet loads of clay blocks (Blender: OBS_BrickStack_A).
    id: 'OBS_BRICK_STACK_A',
    type: ObstacleType.DoubleJump,
    displayName: 'Ziegelstapel',
    visualAssetId: 'OBS_BRICK_STACK_A',
    colliders: [{ center: { x: 0, y: 0.93, z: 0 }, size: { x: 1.25, y: 1.86, z: 0.82 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['bricks', 'pallet'],
  },
  {
    // Reference sheets SEG_21/22/23: Leitpfeiltafel closing a lane, arrows towards the free lane
    // (orientation: guideSigns.ts). Blender: ENV_ChevronSign_A.
    id: 'OBS_LANE_CLOSURE_A',
    type: ObstacleType.LaneChange,
    displayName: 'Spursperrung (Leitpfeiltafel)',
    visualAssetId: 'ENV_CHEVRON_SIGN_A',
    colliders: [{ center: { x: 0, y: 0.7, z: 0 }, size: { x: 1.7, y: 1.4, z: 0.8 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['guide', 'sign'],
  },
  {
    // Change lanes – or double-jump onto the walkable roof and run along it (see CONTAINER_OBSTACLE). Placed only
    // where its lane offers the free run-up for that double jump and a free drop zone behind it; most carry bottles
    // on the roof (level/terrain/roofRunUp.ts).
    id: 'OBS_CONTAINER_A',
    type: ObstacleType.LaneChange,
    displayName: 'Container',
    visualAssetId: 'OBS_CONTAINER_A',
    colliders: [
      {
        center: { x: 0, y: CONTAINER_OBSTACLE.height / 2, z: 0 },
        size: { x: CONTAINER_OBSTACLE.width, y: CONTAINER_OBSTACLE.height, z: CONTAINER_OBSTACLE.length },
      },
    ],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 1,
    tags: ['container'],
    walkableTop: true,
  },
  // Set-piece obstacles of the terrain segments – weight 0, placed by obstacle variants only.
  {
    // SEG_06 "Palette" + "Betonblock": material stored under a scaffold crossbar – stacked right up to the
    // bar (1.6 m), so it cannot be jumped (the bar above blocks) – change lanes.
    id: 'OBS_MATERIAL_STACK_A',
    type: ObstacleType.LaneChange,
    displayName: 'Materialstapel unter Gerüstriegel',
    visualAssetId: 'OBS_MATERIAL_STACK_A',
    colliders: [{ center: { x: 0, y: 0.8, z: 0 }, size: { x: 1.8, y: 1.6, z: 1.2 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 0,
    tags: ['pallet', 'concrete', 'set-piece'],
  },
  {
    // SEG_07 "Kabeltrommel": small cable drum Ø 0.9 m, axis across the lane – a single jump.
    id: 'OBS_CABLE_DRUM_A',
    type: ObstacleType.Jump,
    displayName: 'Kabeltrommel klein',
    visualAssetId: 'OBS_CABLE_DRUM_A',
    colliders: [{ center: { x: 0, y: 0.45, z: 0 }, size: { x: 1.2, y: 0.9, z: 0.9 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 0,
    tags: ['cable', 'set-piece'],
  },
  {
    // SEG_21 "Erdhaufen": heap of excavated soil with stones (Blender: OBS_DirtMound_A) – a single jump.
    id: 'OBS_DIRT_MOUND_A',
    type: ObstacleType.Jump,
    displayName: 'Erdhaufen',
    visualAssetId: 'OBS_DIRT_MOUND_A',
    colliders: [{ center: { x: 0, y: 0.45, z: 0 }, size: { x: 2.0, y: 0.9, z: 1.6 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 0,
    tags: ['earth', 'set-piece'],
  },
  // Set-piece obstacles of the moving-hazard segments (SEG_09–11) – weight 0, placed by obstacle variants only.
  {
    // SEG_11 "Regal (durchrutschen)": pallet rack, first beam level 1.6 m (Blender: OBS_PalletRack_A).
    id: 'OBS_PALLET_RACK_A',
    type: ObstacleType.Slide,
    displayName: 'Palettenregal',
    visualAssetId: 'OBS_PALLET_RACK_A',
    colliders: [
      // beams, cartons and wrapped pallets from 1.6 m up – no jumping over
      { center: { x: 0, y: 2.6, z: 0 }, size: { x: 2.1, y: 2, z: 1.1 } },
      // upright frames at the lane edges – lane changes through them cause a stumble
      { center: { x: -1.05, y: 1.8, z: 0 }, size: { x: 0.08, y: 3.6, z: 1.1 } },
      { center: { x: 1.05, y: 1.8, z: 0 }, size: { x: 0.08, y: 3.6, z: 1.1 } },
    ],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 0,
    tags: ['rack', 'set-piece'],
  },
  {
    // SEG_10 "Schutthaufen": rubble heap (the excavator dumps the same heap into a lane).
    id: 'OBS_RUBBLE_HEAP_A',
    type: ObstacleType.Jump,
    displayName: 'Schutthaufen',
    visualAssetId: 'OBS_RUBBLE_HEAP_A',
    colliders: [
      {
        center: { x: 0, y: HAZARDS_CONFIG.excavator.heap.height / 2, z: 0 },
        size: { x: HAZARDS_CONFIG.excavator.heap.width, y: HAZARDS_CONFIG.excavator.heap.height, z: HAZARDS_CONFIG.excavator.heap.depth },
      },
    ],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 0,
    tags: ['debris', 'set-piece'],
  },
  {
    // SEG_DOZER_60M: the parked dozer (VEH_DOZER_A, engine running) – change lanes on foot, or jump onto it and drive
    // (dozer/DozerManager). Colliders = COLLIDER_BODY / COLLIDER_BLADE of the collision export (DOZER_CONFIG).
    id: DOZER_CONFIG.vehicle.obstacleId,
    type: ObstacleType.LaneChange,
    displayName: 'Planierraupe (geparkt)',
    visualAssetId: DOZER_CONFIG.vehicle.assetId,
    colliders: DOZER_CONFIG.vehicle.colliders,
    laneSpan: 1,
    // A machine is never smashed out of the way: a runner who crashes into it (life left) stays in front of it and,
    // invulnerable after the hit, climbs onto it (DozerManager: protected runners mount from the ground).
    clearedOnHit: false,
    weight: 0,
    tags: ['vehicle', 'dozer', 'set-piece'],
  },
  // Debris of the collapsing building (SEG_COLLAPSE_40M) – weight 0, set pieces only.
  {
    id: 'OBS_DEBRIS_BLOCK_A',
    type: ObstacleType.Jump,
    displayName: 'Betontrümmer',
    visualAssetId: 'OBS_DEBRIS_BLOCK_A',
    colliders: [{ center: { x: 0, y: 0.45, z: 0 }, size: { x: 2, y: 0.9, z: 1.2 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 0,
    tags: ['debris'],
  },
  {
    id: 'OBS_DEBRIS_WALL_A',
    type: ObstacleType.LaneChange,
    displayName: 'Wandstück',
    visualAssetId: 'OBS_DEBRIS_WALL_A',
    colliders: [{ center: { x: 0, y: 1.7, z: 0 }, size: { x: 2, y: 3.4, z: 1 } }],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 0,
    tags: ['debris'],
  },
  {
    id: 'OBS_DEBRIS_SLAB_A',
    type: ObstacleType.Slide,
    displayName: 'Deckenplatte auf Trümmern',
    visualAssetId: 'OBS_DEBRIS_SLAB_A',
    colliders: [
      { center: { x: 0, y: 2.25, z: 0 }, size: { x: 2.2, y: 2.3, z: 0.8 } },
      { center: { x: -1.05, y: 0.55, z: 0 }, size: { x: 0.2, y: 1.1, z: 0.6 } },
      { center: { x: 1.05, y: 0.55, z: 0 }, size: { x: 0.2, y: 1.1, z: 0.6 } },
    ],
    laneSpan: 1,
    clearedOnHit: true,
    weight: 0,
    tags: ['debris'],
  },
];

export class ObstacleCatalog {
  private readonly byId = new Map<string, ObstacleDefinition>();

  constructor(definitions: readonly ObstacleDefinition[]) {
    for (const definition of definitions) {
      if (this.byId.has(definition.id)) throw new Error(`Duplicate obstacle id ${definition.id}`);
      this.byId.set(definition.id, definition);
    }
  }

  get(id: string): ObstacleDefinition {
    const definition = this.byId.get(id);
    if (!definition) throw new Error(`Unknown obstacle id ${id}`);
    return definition;
  }

  ofType(type: ObstacleType): readonly ObstacleDefinition[] {
    return [...this.byId.values()].filter((d) => d.type === type);
  }

  /** Definitions the random generator may use (weight > 0). */
  randomOfType(type: ObstacleType): readonly ObstacleDefinition[] {
    return this.ofType(type).filter((d) => d.weight > 0);
  }

  all(): readonly ObstacleDefinition[] {
    return [...this.byId.values()];
  }

  /** Depth (z extent) of the obstacle's colliders – used for spacing rules. */
  static depthOf(definition: ObstacleDefinition): number {
    let min = Infinity;
    let max = -Infinity;
    for (const c of definition.colliders) {
      min = Math.min(min, c.center.z - c.size.z / 2);
      max = Math.max(max, c.center.z + c.size.z / 2);
    }
    return max - min;
  }
}
